// -@ts-nocheck

import { Collapse, Modal } from "antd";
import { apiHost } from "consts";
import type { ParentData } from "features/cases/blocks/CommonCard/CommonCard.types";
import {
  getCardClassName,
  getCardDimensions,
  getCardShareUrl,
  getEditContentUrlAndQueryId,
  getHasButtons,
  getVariantMeta,
  isHalfTransparent,
  removeHtmlTags,
} from "features/cases/blocks/CommonCard/CommonCard.utils";
import AdminButtons from "features/cases/blocks/CommonCard/elements/AdminButtons";
import AutosaveStatus from "features/cases/blocks/CommonCard/elements/AutosaveStatus";
import CardEditor from "features/cases/blocks/CommonCard/elements/CardEditor";
import CardFooter from "features/cases/blocks/CommonCard/elements/CardFooter";
import CardHeader from "features/cases/blocks/CommonCard/elements/CardHeader";
import EditingButtons from "features/cases/blocks/CommonCard/elements/EditingButtons";
import { usePersonBlock } from "features/cases/blocks/CommonCard/elements/PersonBlock";
import RestoreDraftBanner from "features/cases/blocks/CommonCard/elements/RestoreDraftBanner";
import SocialButtons from "features/cases/blocks/CommonCard/elements/SocialButtons";
import SphereEdit from "features/cases/blocks/CommonCard/elements/SphereEdit";
import ToggleIcon from "features/cases/blocks/CommonCard/elements/ToggleIcon/ToggleIcon";
import VariantEdit from "features/cases/blocks/CommonCard/elements/VariantEdit";
import { useAutosave } from "features/cases/blocks/CommonCard/hooks/useAutosave";
import { useAutosaveLocalDraft } from "features/cases/blocks/CommonCard/hooks/useAutosaveLocalDraft";
import { useCardSave } from "features/cases/blocks/CommonCard/hooks/useCardSave";
import { useOnStatusChange } from "features/cases/blocks/CommonCard/hooks/useOnStatusChange";
import { useOpenModalOnCommentHash } from "features/cases/blocks/CommonCard/hooks/useOpenModalOnCommentHash";
import CommentList from "features/comments/blocks/CommentList";
import MarkdownContent from "features/markdown/elements/MarkdownContent";
import { useMarkOnboardingSeen, useOnboarding } from "features/onboarding";
import { useDataQueryRefetch } from "hooks/useData";
import { useFlag } from "hooks/useFlags";
import { useIsMobile } from "hooks/useIsMobile";
import { useTexts } from "hooks/useTexts";
import { useUser } from "hooks/useUser";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import type { DateDisplayMode } from "ui/DateDisplay";
import ModalWindow from "ui/ModalWindow";
import SimpleCard from "ui/SimpleCard";

import "./CommonCard.css";

const collectivePersonId = 2;
const noop = () => {
  // no operation
};

//@ts-ignore
const CommonCard = ({
  meta = {},
  parentData = {
    sphereId: 0,
  },
  sectionTitle = null,
  description = "",
  title = "",
  mode = "default",
  vmode = "default",
  url = "",
  editUrl = "",
  addUrl = "",
  embedded = false,
  objectColor = "#333333",
  highlightColor = "#ffffff",
  linkButtonText = "Перейти",
  cardContentId = "",
  closeModal = noop,
  closeModalAfterSave,
  onSaved = noop,
  openLinksInNewWindow = false,
  onLinkClick = noop,
  setIsEditing = noop,
  autoModalOpen = false,
  isEditing = false,
  isReviewing = false,
  dateDisplayMode = "modified",
  onAutosaveGuardChange = noop,
}: {
  meta?: any;
  parentData?: ParentData;
  dateDisplayMode?: DateDisplayMode;
  openLinksInNewWindow?: boolean;
  sectionTitle?: string | null;
  title?: string;
  description?: string;
  mode?: "default" | "half" | "quarter" | "wide";
  vmode?: "default" | "short";
  url?: string;
  editUrl?: string;
  addUrl?: string;
  embedded?: boolean;
  objectColor?: string;
  highlightColor?: string;
  linkButtonText?: string;
  cardContentId?: string;
  closeModal?: () => void;
  closeModalAfterSave?: () => void;
  onLinkClick?: () => void;
  isEditing?: boolean;
  setIsEditing?: (val: boolean) => void;
  onSaved?: () => void;
  autoModalOpen?: boolean;
  isReviewing?: boolean;
  onAutosaveGuardChange?: (guard: {
    hasUnsaved: boolean;
    isLocalOnly: boolean;
    discard: () => void;
  }) => void;
}) => {
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();

  const { txt } = useTexts("card");
  const {
    isEditor,
    isAdmin,
    isContributor,
    userId,
    personId,
    isContentEditor,
    isLoggedIn,
    isBannedFromEditing,
    canPublishAsCollective,
  } = useUser();

  const [expanded, setExpanded] = useState(false);
  const [showToggle, _setToggle] = useState(true);

  const isDraft = meta?.isDraft ?? false;
  const isDeleted = meta?.isDeleted ?? false;
  const isModerated = meta?.isModerated ?? false;

  const [isDeletedCurrent, setIsDeletedCurrent] = useState(isDeleted);
  const [isModeratedCurrent, setIsModeratedCurrent] = useState(isModerated);

  const canComment = useFlag("commentsEnabled");

  const moderatorialCommentsEnabled = useFlag("moderatorialCommentsEnabled");

  const hasModeratorialComments =
    (isModeratedCurrent || isModerated) &&
    moderatorialCommentsEnabled &&
    isLoggedIn;

  const isNewCard = addUrl && !cardContentId && !embedded;

  const showComments = !isNewCard && (canComment || hasModeratorialComments);

  const [reviewCount, setReviewCount] = useState(meta?.reviewCount || 0);
  const [reviewedByUser, setReviewedByUser] = useState(
    meta?.reviewedByUser || false,
  );

  const cardContentType = parentData.isAttribute
    ? "attribute"
    : parentData.isProblem
      ? "problem"
      : parentData.isSolution
        ? "solution"
        : undefined;

  const cardId = `${cardContentType}-${cardContentId ?? "new"}`;

  const commentBlock = showComments ? (
    <CommentList
      commentTargetType={cardContentType || ""}
      commentTargetId={Number(cardContentId) || 0}
      isModerated={hasModeratorialComments}
      problemId={parentData.problemId}
      solutionId={parentData.solutionId}
      cardId={parentData.isAttribute ? cardContentId : undefined}
    />
  ) : null;

  const isMyStuff = userId && meta?.user === userId;

  const showMd = isAdmin;

  const isCreating = addUrl && !cardContentId && embedded;
  const isNewAttributeCard = !!(isCreating && parentData.isAttribute);
  const initialTemplate = isNewAttributeCard
    ? parentData.attr?.template || ""
    : "";

  const [editorInitialValue] = useState(description || initialTemplate);
  // Хранит последний системный (не введённый пользователем) текст в редакторе —
  // пока текущее содержимое совпадает с этим значением, смену варианта можно применять.
  const autoFilledTextRef = useRef(description ? "" : initialTemplate);

  const linkTarget = openLinksInNewWindow ? "_blank" : "_self";

  const refEditor = useRef(null);
  const refInput = useRef(null);

  const isNewProblem =
    isContributor && addUrl && !cardContentId && parentData?.isProblem
      ? true
      : false;

  const isNewSolution =
    isContributor && addUrl && !cardContentId && parentData?.isSolution
      ? true
      : false;

  const preopenModal = (isNewProblem || isNewSolution) && autoModalOpen;

  const [isModalOpen, setIsModalOpen] = useState(preopenModal);
  const [isEditingCard, setIsEditingCard] = useState(false);

  const [titleInput, setTitleInput] = useState(title);

  const isCollectiveContent = meta?.author === collectivePersonId;

  // Коллективным контентом можно управлять только при действующем праве
  // публиковать от имени коллективного разума; одного авторства недостаточно.
  const canActOnOwnContent =
    isMyStuff && (!isCollectiveContent || canPublishAsCollective);

  const isReviewingMode =
    isContentEditor && !isNewCard && !isCreating && !isMyStuff;

  const canEditStuff =
    !isBannedFromEditing &&
    !!(
      canActOnOwnContent ||
      isNewSolution ||
      isNewProblem ||
      isCreating ||
      isNewCard ||
      (isReviewingMode && isCollectiveContent)
    );

  const canEdit = !!(
    isAdmin ||
    (isReviewingMode && isCollectiveContent) ||
    (isContributor && canEditStuff)
  );

  const desktopOrNewCard = isNewSolution || isNewProblem;

  const showEdit = isAdmin && !!editUrl;

  const {
    personBlock: PersonBlock,
    avatarElement,
    isUsingCollectivePerson,
  } = usePersonBlock(meta, isCreating);

  const { variantString, variantId } = getVariantMeta(
    parentData.attributeVariant,
  );

  const [selectedVariant, setSelectedVariant] = useState<number | null>(
    variantId || null,
  );

  const markOnboardingSeen = useMarkOnboardingSeen();
  const refSaveButton = useRef(null);
  const refSphereInput = useRef(null);

  const { tourNode: onboardingBlock } = useOnboarding({
    topic: "problem-create",
    refs: [undefined, refSphereInput, refSaveButton],
    condition: isNewProblem && autoModalOpen,
    onOpen: () => markOnboardingSeen("reading"),
  });

  const { refetch: refetchGeneral } = useDataQueryRefetch(`general`);

  const [selectedSphere, setSelectedSphere] = useState<number | null>(
    parentData?.sphereId || 0,
  );

  const baseHeight = 240;

  const isProblem =
    sectionTitle === txt("problem", "Проблема") || parentData.isProblem;
  const isSolution =
    sectionTitle?.startsWith(txt("solution", "Решение")) ||
    parentData.isSolution;
  const isCard = !isProblem && !isSolution;

  // Автосохранение создаёт запись на backend до того, как cardContentId
  // приходит из props (явного сохранения/навигации ещё не было) — дальше все
  // сохранения (автосохранение и ручное) должны редактировать именно её, а не
  // создавать вторую.
  const [autosavedId, setAutosavedId] = useState<string | undefined>(undefined);
  const effectiveContentId = cardContentId || autosavedId;

  const { editContentUrl, queryId } = getEditContentUrlAndQueryId(
    parentData.isProblem,
    parentData.isSolution,
    parentData.isAttribute,
    effectiveContentId,
    parentData.problemId,
    apiHost,
  );

  const isEditable =
    isContributor &&
    !!editContentUrl &&
    canEdit &&
    (desktopOrNewCard || isEditing);

  const [editedContent, setEditedContent] = useState<string>(
    description || initialTemplate,
  );

  const handleVariantChange = useCallback(
    (variantIdSelected: number | null, variantTemplate?: string | null) => {
      setSelectedVariant(variantIdSelected);

      if (!isNewAttributeCard) return;

      const nextTemplate = variantTemplate || parentData.attr?.template || "";
      const isUntouched = editedContent === autoFilledTextRef.current;

      if (isUntouched && nextTemplate !== editedContent) {
        refEditor.current?.setMarkdown(nextTemplate);
        setEditedContent(nextTemplate);
        autoFilledTextRef.current = nextTemplate;
      }
    },
    [isNewAttributeCard, editedContent, parentData.attr?.template],
  );

  // Выключено по умолчанию везде (см. specs/features/autosave) — раскатка
  // отдельным решением, а не автоматически.
  const autosaveEnabled = useFlag("autosaveEnabled");

  // Автосохранение решает, куда писать, по текущему черновик-статусу ЭТОЙ
  // строки (не родителя): новый контент и существующие черновики безопасно
  // автосохранять на backend (черновик и так виден только владельцу); правка
  // уже опубликованного — только в localStorage, иначе недописанная правка
  // станет видна всем раньше явного сохранения.
  const isBrandNewContent = !!isCreating || isNewProblem || isNewSolution;
  const isEditorVisible = autosaveEnabled && isEditable && embedded;
  const canAutosaveToBackend =
    isEditorVisible && (isBrandNewContent || isDraft);
  const canAutosaveToLocal = isEditorVisible && !isBrandNewContent && !isDraft;

  const autosave = useAutosave({
    enabled: canAutosaveToBackend,
    hasExistingId: !!effectiveContentId,
    editContentUrl,
    queryId,
    parentData,
    titleInput,
    editedContent,
    selectedSphere,
    selectedVariant,
    isUsingCollectivePerson,
    personId,
    onCreated: setAutosavedId,
  });

  const localAutosave = useAutosaveLocalDraft({
    enabled: canAutosaveToLocal,
    storageKey: `autosave:${cardContentType}:${cardContentId}`,
    titleInput,
    editedContent,
    selectedSphere,
    selectedVariant,
  });

  const autosaveStatus = canAutosaveToBackend
    ? autosave.status
    : canAutosaveToLocal
      ? localAutosave.status
      : "idle";

  const lastAutosavedAt = canAutosaveToBackend
    ? autosave.lastSavedAt
    : canAutosaveToLocal
      ? localAutosave.lastSavedAt
      : null;

  const hasUnsavedAutosave = canAutosaveToBackend
    ? autosave.hasUnsavedChanges
    : canAutosaveToLocal
      ? localAutosave.hasUnsavedChanges
      : false;

  // Пользователь подтвердил закрытие без сохранения (диалог в closeCardModal
  // родительского экземпляра) — откатываем то, что автосохранение ещё не
  // донесло: для черновика/нового контента просто отменяем отложенный тик, для
  // уже опубликованного — удаляем локальный черновик-подстраховку.
  const discardUnsavedAutosave = useCallback(() => {
    if (canAutosaveToBackend) autosave.cancelPendingChanges();
    if (canAutosaveToLocal) localAutosave.discardCurrentDraft();
  }, [canAutosaveToBackend, canAutosaveToLocal, autosave, localAutosave]);

  // closeCardModal живёт в родительском (не embedded) экземпляре и не видит
  // состояние автосохранения этого (embedded) экземпляра — передаём его через
  // проп-колбэк, а не поднимаем сами хуки наружу.
  useEffect(() => {
    onAutosaveGuardChange({
      hasUnsaved: hasUnsavedAutosave,
      isLocalOnly: canAutosaveToLocal,
      discard: discardUnsavedAutosave,
    });
  }, [
    hasUnsavedAutosave,
    canAutosaveToLocal,
    discardUnsavedAutosave,
    onAutosaveGuardChange,
  ]);

  const onRestoreLocalDraft = useCallback(() => {
    const draft = localAutosave.pendingRestore;

    if (!draft) return;

    setTitleInput(draft.title);
    setEditedContent(draft.description);
    refEditor.current?.setMarkdown(draft.description);
    setSelectedSphere(draft.sphere);
    setSelectedVariant(draft.variant);
    localAutosave.discardRestore();
  }, [localAutosave]);

  // Явное сохранение уже синхронизировало backend/изменило живую запись —
  // автосохранению незачем повторять тот же запрос или держать локальный
  // черновик дальше.
  const handleExplicitSaved = useCallback(() => {
    autosave.resyncSnapshot();
    localAutosave.clearAfterExplicitSave();
    onSaved?.();
  }, [autosave, localAutosave, onSaved]);

  const {
    onSaveAndPublishClick,
    onSaveToDraftClick,
    onSaveEditedClick,
    canShowPublishButton,
    canShowSaveToDraftButton,
    canShowSaveEditedButton,
    contentIsSubmittable,
    submitTooltipText,
    contentValidation,
  } = useCardSave({
    editContentUrl,
    parentData,
    selectedSphere,
    selectedVariant,
    isUsingCollectivePerson,
    editedContent,
    titleInput,
    isCard,
    isDraft,
    isMyStuff: canActOnOwnContent,
    isReviewingMode,
    isCreating,
    queryId,
    // Автозакрытие после удачного ЯВНОГО сохранения не должно спрашивать
    // подтверждение — на этот момент сохранять уже нечего. Guard в closeModal
    // актуален только для X/Escape/"Отмена", не для этого авто-закрытия.
    closeModal: closeModalAfterSave ?? closeModal,
    onSaved: handleExplicitSaved,
    refetchGeneral,
  });

  const editingButtons = (
    <>
      <AutosaveStatus
        status={autosaveStatus}
        lastSavedAt={lastAutosavedAt}
      />
      <EditingButtons
        canShowSaveEditedButton={canShowSaveEditedButton}
        canShowSaveToDraftButton={canShowSaveToDraftButton}
        canShowPublishButton={canShowPublishButton}
        contentIsSubmittable={contentIsSubmittable}
        submitTooltipText={submitTooltipText}
        isReviewingMode={isReviewingMode}
        onSaveEditedClick={onSaveEditedClick}
        onSaveToDraftClick={onSaveToDraftClick}
        onSaveAndPublishClick={onSaveAndPublishClick}
        closeModal={closeModal}
      />
    </>
  );

  const { width, minWidth, height, minHeight } = getCardDimensions({
    mode,
    isMobile,
    vmode,
    expanded,
    embedded,
    baseHeight,
  });

  const proceedToCloseCardModal = useCallback(() => {
    if (preopenModal) {
      navigate(-1);

      return;
    }
    setIsModalOpen(false);
    setIsEditingCard(false);
    setIsEditing(false);
  }, [
    setIsModalOpen,
    closeModal,
    setIsEditingCard,
    setIsEditing,
    preopenModal,
  ]);

  // Состояние автосохранения живёт во вложенном (embedded) экземпляре,
  // а закрытие — здесь; мост между ними — этот ref, который embedded-экземпляр
  // обновляет через onAutosaveGuardChange (см. embeddedCard ниже).
  const autosaveGuardRef = useRef({
    hasUnsaved: false,
    isLocalOnly: false,
    discard: noop,
  });

  const handleAutosaveGuardChange = useCallback(
    (guard: {
      hasUnsaved: boolean;
      isLocalOnly: boolean;
      discard: () => void;
    }) => {
      autosaveGuardRef.current = guard;
    },
    [],
  );

  const closeCardModal = useCallback(() => {
    const { hasUnsaved, isLocalOnly, discard } = autosaveGuardRef.current;

    if (!hasUnsaved) {
      proceedToCloseCardModal();

      return;
    }

    Modal.confirm({
      title: "Закрыть без сохранения?",
      content: isLocalOnly
        ? "Черновик сохранён только в этом браузере. Если закрыть сейчас, он будет удалён, а опубликованная версия останется прежней."
        : "Последние изменения ещё не сохранены и будут потеряны, если закрыть сейчас.",
      okText: "Закрыть",
      cancelText: "Остаться",
      okButtonProps: { danger: true },
      onOk: () => {
        discard();
        proceedToCloseCardModal();
      },
    });
  }, [proceedToCloseCardModal]);

  const onCardSaved = useCallback(() => {
    setReviewCount((count) => count + 1);
    setReviewedByUser(true);
    onSaved?.();
  }, [isReviewingMode, reviewCount, onSaved]);

  // NEW CARD

  const embeddedCard = (
    <CommonCard
      parentData={parentData}
      cardContentId={cardContentId}
      meta={meta}
      description={description}
      mode={mode}
      vmode={vmode}
      url={url}
      editUrl={editUrl}
      addUrl={addUrl}
      embedded={true}
      isEditing={isEditingCard}
      setIsEditing={setIsEditingCard}
      title={title}
      closeModal={closeCardModal}
      closeModalAfterSave={proceedToCloseCardModal}
      isReviewing={isReviewing}
      onSaved={onCardSaved}
      onAutosaveGuardChange={handleAutosaveGuardChange}
    />
  );

  const toggleClick = () => {
    if (isMobile) {
      setIsEditingCard(false);
      setIsModalOpen(true);
    } else {
      setExpanded(!expanded);
    }
  };

  const editButtonClick = () => {
    setIsEditingCard(true);
    setIsModalOpen(true);
  };

  const onStatusChange = useOnStatusChange(parentData, queryId);

  const [hasOverflow, setHasOverflow] = useState(false);

  const showTopToggle =
    hasOverflow && isMobile && !expanded && !embedded && showToggle;

  const showBottomToggle =
    hasOverflow &&
    !isMobile &&
    ((isEditable && !embedded) || (!embedded && showToggle));

  const backgroundColor = isNewCard ? "#f0f0f077" : "#ffffff";

  const instructionText = parentData.attr?.description ?? null;

  const instructionModalContent = isContributor ? instructionText : null;

  // На мобильном в свёрнутом блоке снизу — комментарии в режиме просмотра,
  // инструкции в режиме редактирования (как в десктопной правой панели).
  const mobileSecondaryTitle = isEditable
    ? txt("instructions", "Инструкции")
    : txt("comments", "Комментарии");
  const mobileSecondaryContent = isEditable
    ? instructionModalContent && (
        <MarkdownContent content={instructionModalContent} />
      )
    : commentBlock;
  const showMobileSecondary =
    embedded &&
    isMobile &&
    (isEditable ? !!instructionModalContent : showComments);

  // Ссылка на конкретный комментарий адресует "{cardId}~comment-{id}" — только
  // тогда авто-раскрываем блок, иначе он всегда свёрнут по умолчанию.
  const commentHashTargetsThisCard =
    showComments && location.hash.slice(1).startsWith(`${cardId}~comment-`);

  const toggleText = (
    <ToggleIcon
      isMobile={isMobile}
      expanded={expanded}
    />
  );

  // add card
  const cardContent = isNewCard ? (
    <div>
      {isContributor && onboardingBlock}
      <div
        className="card-add-icon"
        onClick={editButtonClick}
      >
        +
      </div>

      {!embedded && (
        <ModalWindow
          overlayTitle={sectionTitle}
          title={null}
          content2={showComments ? commentBlock : instructionModalContent}
          content={embeddedCard}
          isModalOpen={isModalOpen}
          setIsModalOpen={closeCardModal}
          overlayColor={objectColor}
          showCloseBtn={!preopenModal}
        />
      )}
    </div>
  ) : (
    <>
      {(!embedded || (isMobile && !isEditable)) && (
        <div>
          <SocialButtons
            meta={meta}
            cardContentType={cardContentType}
            cardContentId={cardContentId}
            isReviewing={isReviewing}
            isCollectiveContent={isCollectiveContent}
            isMyStuff={!!isMyStuff}
            reviewCount={reviewCount}
            reviewedByUser={reviewedByUser}
            onReviewToggle={onStatusChange}
          />
          <AdminButtons
            cardContentType={cardContentType}
            cardContentId={cardContentId}
            isDeletedCurrent={isDeletedCurrent}
            setIsDeletedCurrent={setIsDeletedCurrent}
            isModeratedCurrent={isModeratedCurrent}
            setIsModeratedCurrent={setIsModeratedCurrent}
            onStatusChange={onStatusChange}
          />
        </div>
      )}

      <div className={embedded && isMobile ? "h100 card-mobile-body" : "h100"}>
        {!embedded && (
          <ModalWindow
            overlayTitle={sectionTitle}
            title={null}
            content={embeddedCard}
            content2={showComments ? commentBlock : instructionModalContent}
            isModalOpen={isModalOpen}
            setIsModalOpen={closeCardModal}
            overlayColor={objectColor}
          />
        )}

        {(!isEditable || !embedded) && (
          <MarkdownContent
            content={
              (title ? "#### " + removeHtmlTags(title) + "\n\n" : "") +
              removeHtmlTags(description)
            }
          />
        )}

        {isEditable && (
          <>
            <div className="h100">
              {embedded ? (
                <>
                  {canAutosaveToLocal && localAutosave.pendingRestore && (
                    <RestoreDraftBanner
                      onRestore={onRestoreLocalDraft}
                      onDiscard={localAutosave.discardRestore}
                    />
                  )}
                  <CardEditor
                    initialValue={editorInitialValue}
                    showMd={showMd}
                    titleValue={titleInput}
                    onTitleChange={setTitleInput}
                    onContentChange={setEditedContent}
                    sphereSelector={
                      <SphereEdit
                        value={selectedSphere}
                        onChange={setSelectedSphere}
                      />
                    }
                    variantSelector={
                      <VariantEdit
                        value={selectedVariant}
                        onChange={handleVariantChange}
                        attrId={parentData?.attr?.id}
                      />
                    }
                    showSphereSelector={false}
                    editorRef={refEditor}
                    titleInputRef={refInput}
                    sphereInputRef={refSphereInput}
                    validation={contentValidation}
                    objectType={cardContentType}
                  />
                </>
              ) : null}
            </div>
          </>
        )}

        {showMobileSecondary && (
          <Collapse
            className="card-comments-collapse"
            defaultActiveKey={
              !isEditable && commentHashTargetsThisCard
                ? ["comments"]
                : undefined
            }
            items={[
              {
                key: "comments",
                label: mobileSecondaryTitle,
                children: mobileSecondaryContent,
              },
            ]}
          />
        )}
      </div>
    </>
  );

  const hasButtons = getHasButtons({
    canComment,
    desktopOrNewCard,
    embedded,
    showComments,
    url,
    isEditor,
    editUrl,
    addUrl,
    showBottomToggle,
    canEdit,
    showEdit,
    isEditing,
  });

  const cardFooter = (
    <CardFooter
      isNewCard={isNewCard}
      parentData={parentData}
      hasButtons={hasButtons}
      variantString={variantString}
      showEdit={showEdit}
      editUrl={editUrl}
      url={url}
      linkTarget={linkTarget}
      onLinkClick={onLinkClick}
      linkButtonText={linkButtonText}
      embedded={embedded}
      canEdit={canEdit}
      addUrl={addUrl}
      desktopOrNewCard={desktopOrNewCard}
      isEditing={isEditing}
      editingButtons={editingButtons}
      editButtonClick={editButtonClick}
      showComments={showComments}
      showBottomToggle={showBottomToggle}
      toggleClick={toggleClick}
      toggleText={toggleText}
    />
  );

  useOpenModalOnCommentHash(cardId, showComments, setIsModalOpen);

  const cardUrl = getCardShareUrl(parentData, cardContentId);

  const cardHeader = isNewCard ? null : (
    <CardHeader
      showTopToggle={showTopToggle}
      avatarElement={avatarElement}
      PersonBlock={PersonBlock}
      meta={meta}
      cardUrl={cardUrl}
      toggleClick={toggleClick}
      toggleText={toggleText}
      dateDisplayMode={dateDisplayMode}
    />
  );

  const halfTransparent = isHalfTransparent(
    isDraft,
    isModeratedCurrent,
    isDeletedCurrent,
    embedded,
  );
  const cardClassName = getCardClassName(
    isModeratedCurrent,
    isDeletedCurrent,
    embedded,
    halfTransparent,
    isNewCard,
  );

  return (
    <SimpleCard
      cardId={cardId}
      className={cardClassName}
      style={{
        width,
        minWidth,
        height,
        minHeight,
        backgroundColor,
      }}
      scrollable={embedded}
      header={cardHeader}
      headerColor={
        parentData.isProblem || parentData.isSolution
          ? highlightColor
          : undefined
      }
      content={cardContent}
      footer={cardFooter}
      onHasOverflow={() => setHasOverflow(true)}
      wrapContent={!embedded}
    />
  );
};

export default CommonCard;
